import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, apiRaw, categoryText, enc, isBinCode, type Lot } from '../api';
import { useAuth } from '../auth';
import { LotPhotos } from '../components/Photos';
import { ScanField } from '../components/ScanField';
import { ErrorBox, Field, Loading, LotKindBadge, Money, SOURCE_LABELS, Success, Val, fmtDate, locationText } from '../components/ui';
import { Icon } from '../components/Icon';
import { CompletenessFields, completenessBody, completenessFromLot, completenessProblem, type CompletenessDraft } from '../components/Completeness';
import { useBins, useConditions, useLocations, useSettings } from '../hooks';

export function LotDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['lot', id], queryFn: () => api<{ lot: Lot }>(`/lots/${id}`).then((r) => r.lot) });
  const setLot = (l: Lot) => qc.setQueryData(['lot', id], l);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const lot = q.data!;
  return <LotView lot={lot} setLot={setLot} />;
}

export function LotView({ lot, setLot }: { lot: Lot; setLot: (l: Lot) => void }) {
  const { user } = useAuth();
  const isVoid = lot.status === 'void';
  const [editing, setEditing] = useState(false);

  return (
    <div className="narrow">
      <div className="lot-head">
        <div>
          <div className="muted small">Lot label</div>
          <div className="lot-number mono">{lot.lotNumber}</div>
        </div>
        <div className="lot-loc">
          <div className="muted small">Current location</div>
          <div className="loc-big">{lot.location ? lot.location.code : <span className="missing">none</span>}</div>
          <div className="muted small">{locationText(lot.location)}</div>
          {lot.bin && (
            <Link to={`/bins/${lot.bin.id}`} className="bin-chip small">
              <Icon name="bin" /> in {lot.bin.code}
            </Link>
          )}
        </div>
      </div>
      {isVoid && <div className="alert alert-error">This lot is VOID: {lot.voidReason}. Its label number stays retired and cannot be reused.</div>}

      <section className="card">
        <h2>Location</h2>
        {!isVoid && <MoveControl lot={lot} onMoved={setLot} />}
        {lot.locationHistory.length > 0 && (
          <details>
            <summary>Location history ({lot.locationHistory.length})</summary>
            <ul className="history">
              {lot.locationHistory.map((h, i) => (
                <li key={i}>
                  {fmtDate(h.movedAt)}: {h.fromCode ?? '—'} → <strong>{h.toCode ?? '— none'}</strong>
                  {h.binCode ? ` in ${h.binCode}` : ''} by {h.movedByName}
                  {h.note ? ` (${h.note})` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <section className="card">
        <h2>Photos</h2>
        <LotPhotos lot={lot} onChange={setLot} readOnly={isVoid} />
      </section>

      <section className="card">
        <div className="row between">
          <h2>Details</h2>
          {!isVoid && !editing && (
            <button className="btn btn-small" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
        {editing ? (
          <LotEdit lot={lot} onDone={(l) => (l && setLot(l), setEditing(false))} />
        ) : (
          <dl className="dl">
            <dt>Title</dt>
            <dd>
              <Val v={lot.title} />{' '}
              <span className="muted small">
                ({SOURCE_LABELS[lot.titleSource]}
                {lot.titleSourceUrl && (
                  <>
                    {' · '}
                    <a href={lot.titleSourceUrl} target="_blank" rel="noreferrer">
                      page
                    </a>
                  </>
                )}
                )
              </span>
            </dd>
            <dt>Product</dt>
            <dd>
              {lot.product ? (
                <>
                  <Link to={`/products/${lot.productId}`} className="mono">
                    {lot.productItemNumber}
                  </Link>{' '}
                  <span className="muted small">{lot.product.listingTitle ?? lot.product.manifestTitle ?? ''}</span>
                </>
              ) : (
                <span className="muted">None: mixed/parts lot</span>
              )}{' '}
              <LotKindBadge lot={lot} />
            </dd>
            {lot.kind === 'mixed' && (
              <>
                <dt>Pieces in this lot</dt>
                <dd className="prewrap">{lot.contents}</dd>
              </>
            )}
            {lot.completeness === 'incomplete' && (
              <>
                <dt>Missing</dt>
                <dd className="prewrap">
                  {lot.missingParts}
                  <div className="muted small">
                    {lot.piecesPresent != null ? `${lot.piecesPresent} of ${lot.piecesTotal} pieces here` : 'More than half of the item is here (not counted)'}
                  </div>
                </dd>
              </>
            )}
            <dt>Condition</dt>
            <dd>
              <Val v={lot.conditionLabel} />
            </dd>
            <dt>Colour</dt>
            <dd>
              <Val v={lot.colour} />
            </dd>
            <dt>Size / model</dt>
            <dd>
              <Val v={lot.sizeModel} />
            </dd>
            <dt>Lot inspection</dt>
            <dd className="prewrap">
              <Val v={lot.details} />
            </dd>
            <dt>Costco description</dt>
            <dd className="prewrap">
              <Val v={lot.costcoDescription} />{' '}
              {lot.costcoDescription && (
                <span className="muted small">
                  ({SOURCE_LABELS[lot.costcoDescriptionSource]}
                  {lot.costcoDescriptionUrl && (
                    <>
                      {' · '}
                      <a href={lot.costcoDescriptionUrl} target="_blank" rel="noreferrer">
                        page
                      </a>
                    </>
                  )}
                  )
                </span>
              )}
            </dd>
            <dt>Category</dt>
            <dd>
              <Val v={categoryText(lot.mainCategory, lot.realSubcategory)} missing="none" />
            </dd>
            <dt>Retail price</dt>
            <dd>
              <Money amount={lot.referencePrice} currency={lot.referenceCurrency} /> <span className="muted small">({SOURCE_LABELS[lot.referencePriceSource]})</span>
            </dd>
            <dt>Cost</dt>
            <dd>
              <Money amount={lot.cost} currency={lot.costCurrency} />{' '}
              <span className="muted small">
                {lot.costSource === 'estimated' ? `(estimated: ${lot.costPercent}% of retail)` : lot.costSource === 'manifest' ? '(from the manifest)' : ''}
              </span>
            </dd>
            <dt>Starting bid / reserve</dt>
            <dd>
              <Money amount={lot.startingBid} currency={lot.bidCurrency} /> / <Money amount={lot.reservePrice} currency={lot.bidCurrency} />
            </dd>
            <dt>Created</dt>
            <dd>
              {fmtDate(lot.createdAt)} by {lot.createdByName}
            </dd>
            {lot.updatedByName && (
              <>
                <dt>Last changed</dt>
                <dd>
                  {fmtDate(lot.updatedAt)} by {lot.updatedByName}
                </dd>
              </>
            )}
          </dl>
        )}
      </section>

      {user?.role === 'admin' && !isVoid && <VoidControl lot={lot} onDone={setLot} />}
    </div>
  );
}

function MoveControl({ lot, onMoved }: { lot: Lot; onMoved: (l: Lot) => void }) {
  const locations = useLocations();
  const bins = useBins();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const move = async (locationId: string | null) => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const r = await api<{ lot: Lot; unchanged: boolean; to: string | null; removedFromBin: string | null }>('/lots/move', { body: { lotId: lot.id, locationId } });
      onMoved(r.lot);
      setMsg(r.unchanged ? 'Already at that location.' : `Moved to ${r.to ?? 'no location'}${r.removedFromBin ? ` and taken out of ${r.removedFromBin}` : ''}.`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const scan = async (code: string) => {
    if (isBinCode(code)) return putInBin(code);
    const { status, body } = await apiRaw(`/locations/by-code/${enc(code)}`).catch(() => ({ status: 0, body: null }));
    if (status === 200) move(body.location.id);
    else setError(new Error(status === 404 ? `No location with code "${code}"` : 'Could not reach the server'));
  };
  const putInBin = async (code: string, binId?: string) => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const id = binId ?? (await api<{ bin: { id: string } }>(`/bins/by-code/${enc(code)}`)).bin.id;
      const r = await api<{ unchanged: boolean; binCode: string }>(`/bins/${id}/pack`, { body: { lotId: lot.id } });
      onMoved((await api<{ lot: Lot }>(`/lots/${lot.id}`)).lot);
      setMsg(r.unchanged ? `Already in ${r.binCode}.` : `Put into ${r.binCode}.`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {lot.bin && <p className="small muted">Moving this lot to a location on its own takes it out of {lot.bin.code}.</p>}
      <ScanField kind="location" label="MOVE TO LOCATION OR BIN" onScan={scan} busy={busy} />
      <div className="row gap">
        <select
          disabled={busy}
          value=""
          onChange={(e) => {
            const v = e.target.value;
            if (v.startsWith('bin:')) putInBin('', v.slice(4));
            else if (v) move(v);
          }}
          aria-label="Move to location or bin"
        >
          <option value="">…or choose a location or bin</option>
          {!!bins.data?.length && (
            <optgroup label="Bins">
              {bins.data.map((b) => (
                <option key={b.id} value={`bin:${b.id}`}>
                  <Icon name="bin" /> {b.code}
                  {b.locationCode ? ` (at ${b.locationCode})` : ''}
                </option>
              ))}
            </optgroup>
          )}
          <optgroup label="Locations">
            {locations.data?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.code}
              </option>
            ))}
          </optgroup>
        </select>
      </div>
      {msg && <Success>{msg}</Success>}
      <ErrorBox error={error} />
    </div>
  );
}

function LotEdit({ lot, onDone }: { lot: Lot; onDone: (l: Lot | null) => void }) {
  const conditions = useConditions();
  const settings = useSettings();
  const cur = settings.data?.defaultCurrency ?? 'CAD';
  const [f, setF] = useState({
    title: lot.title ?? '',
    conditionId: lot.conditionId ?? '',
    colour: lot.colour ?? '',
    sizeModel: lot.sizeModel ?? '',
    details: lot.details ?? '',
    costcoDescription: lot.costcoDescription ?? '',
    referencePrice: lot.referencePrice ?? '',
    referenceCurrency: lot.referenceCurrency ?? '',
    startingBid: lot.startingBid ?? '',
    reservePrice: lot.reservePrice ?? '',
    bidCurrency: lot.bidCurrency ?? '',
    contents: lot.contents ?? '',
  });
  const [cd, setCd] = useState<CompletenessDraft>(() => completenessFromLot(lot));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => setError(null), [f, cd]);
  const mixed = lot.kind === 'mixed';
  const problem = mixed ? null : completenessProblem(cd);
  const inp = (k: keyof typeof f) => ({ value: f[k], onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value }) });
  const currencies = [...new Set([cur, 'CAD', 'USD', f.referenceCurrency, f.bidCurrency].filter(Boolean))];

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || problem) return;
    setBusy(true);
    try {
      const r = await api<{ lot: Lot }>(`/lots/${lot.id}`, {
        method: 'PATCH',
        body: {
          version: lot.version,
          ...(mixed ? { contents: f.contents.trim() || null } : completenessBody(cd)),
          title: f.title || null,
          conditionId: f.conditionId || null,
          colour: f.colour || null,
          sizeModel: f.sizeModel || null,
          details: f.details || null,
          costcoDescription: f.costcoDescription.trim() || null,
          referencePrice: f.referencePrice || null,
          referenceCurrency: f.referencePrice ? f.referenceCurrency || null : null,
          startingBid: f.startingBid || null,
          reservePrice: f.reservePrice || null,
          bidCurrency: f.startingBid || f.reservePrice ? f.bidCurrency || null : null,
        },
      });
      onDone(r.lot);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save}>
      {mixed ? (
        <Field label="Pieces in this lot (one per line)">
          <textarea {...inp('contents')} rows={4} maxLength={5000} />
        </Field>
      ) : (
        <CompletenessFields value={cd} onChange={setCd} />
      )}
      <Field label="Title" hint={cd.completeness === 'incomplete' ? "Say it's incomplete: the complete item's title is not accepted." : undefined}>
        <input {...inp('title')} maxLength={500} />
      </Field>
      <div className="grid2">
        <Field label="Condition">
          <select {...inp('conditionId')}>
            <option value="">— not set —</option>
            {conditions.data
              ?.filter((c) => c.active || c.id === lot.conditionId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Colour">
          <input {...inp('colour')} />
        </Field>
      </div>
      <Field label="Size / model">
        <input {...inp('sizeModel')} />
      </Field>
      <Field label="Lot inspection (what you checked)">
        <textarea {...inp('details')} rows={3} />
      </Field>
      {!mixed && (
        <Field label="Costco description" hint="Changing this text marks it as edited by you.">
          <textarea {...inp('costcoDescription')} rows={5} maxLength={10000} />
        </Field>
      )}
      <div className="grid2">
        <Field label="Retail price" hint={lot.costSource === 'manifest' ? undefined : 'The estimated cost follows a change to this.'}>
          <input {...inp('referencePrice')} inputMode="decimal" />
        </Field>
        <Field label="Currency">
          <select {...inp('referenceCurrency')}>
            <option value="">—</option>
            {currencies.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid3">
        <Field label="Starting bid">
          <input {...inp('startingBid')} inputMode="decimal" />
        </Field>
        <Field label="Reserve">
          <input {...inp('reservePrice')} inputMode="decimal" />
        </Field>
        <Field label="Bid currency">
          <select {...inp('bidCurrency')}>
            <option value="">—</option>
            {currencies.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
      </div>
      <ErrorBox error={error} />
      <div className="row gap">
        <button className="btn btn-primary" disabled={busy || !!problem}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => onDone(null)} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function VoidControl({ lot, onDone }: { lot: Lot; onDone: (l: Lot) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <details className="card danger">
      <summary>Void this lot (administrator)</summary>
      <p className="small">Voiding keeps the record and permanently retires label {lot.lotNumber}. Use only for mistakes or items that will never be sold.</p>
      <Field label="Reason (required)">
        <input value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <ErrorBox error={error} />
      <button
        className="btn btn-danger"
        disabled={busy || reason.trim().length < 3}
        onClick={async () => {
          if (!confirm(`Void lot ${lot.lotNumber}? This cannot be undone.`)) return;
          setBusy(true);
          try {
            onDone((await api<{ lot: Lot }>(`/lots/${lot.id}/void`, { body: { reason } })).lot);
          } catch (e) {
            setError(e);
          } finally {
            setBusy(false);
          }
        }}
      >
        Void lot
      </button>
    </details>
  );
}
